import { useState, useMemo, useEffect } from 'react';
import { Box, Flex, Text, Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import tinycolor from 'tinycolor2';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../stateManagement/SettingsContext';
import { Layout } from '../templates';
import { SearchBar } from '../organisms/SearchBar';
import { CustomText } from '../atoms';
import { Pagination } from '../molecules/Pagination';
import { Settings } from '../../interfaces';
import { RelatedSectionsPreview } from '../organisms/RelatedSectionPreview';

const ITEMS_PER_PAGE = 10; // Number of FAQs per page
type SectionPagePreviewProps = {
  onClickLandingPageButton: () => void;
  onClickMyTickets: () => void;
  onContactUsClick: () => void;
  settings: Settings;
  titleForSection: string;
  secondaryAccentColor?: string;
};

export const SectionPagePreview: React.FC<SectionPagePreviewProps> = ({
  onClickLandingPageButton,
  onClickMyTickets,
  onContactUsClick,
  settings,
  titleForSection,
  secondaryAccentColor,
}) => {
  const { sectionsData } = useSettings();

  const [currentPage, setCurrentPage] = useState(1);
  const [sectionBannerUrl, setSectionBannerUrl] = useState<string | null>(null);
  const [sectionName, setSectionName] = useState<string>('');
  // const language = 'en';
  const { t }: { t: (key: string, options?: { returnObjects: boolean }) => any } = useTranslation();
  const helperTexts = t('data.helperTexts', { returnObjects: true });
  const categoryHeadings = t('data.categoryHeadings', { returnObjects: true });

  const lightenedBorderColor = useMemo(() => {
    return tinycolor(settings?.configs.secondaryTextColor).lighten(90).toString();
  }, [settings?.configs.secondaryTextColor]);

  // const sectionToDisplay = useMemo(() => {
  //   return titleForSection
  //     ? sectionsData?.find(
  //         section => section?.section_name.toLowerCase().replace(/\s+/g, '-') === titleForSection,
  //       )
  //     : null;
  // }, [titleForSection, sectionsData]);

  // const sectionToDisplay = useMemo(() => {
  //   const sectionSlugToCheck = sectionName || titleForSection;
  //   console.log('sectionName', sectionName);
  //   console.log('sectionsData', sectionsData);

  //   if (!sectionSlugToCheck) return null;

  //   // 1. Try matching by slug of the name
  //   const bySlug = sectionsData?.find(
  //     section => section?.section_name.toLowerCase().replace(/\s+/g, '-') === sectionSlugToCheck,
  //   );

  //   console.log('bySlug', bySlug);
  //   // 2. If not found — try matching by section_id directly (in case the URL has ID)
  //   if (!bySlug) {
  //     const byId = sectionsData?.find(section => section?.section_id === sectionSlugToCheck);
  //     return byId || null;
  //   }

  //   return bySlug;
  // }, [sectionsData, titleForSection, sectionName]);
  const sectionToDisplay = useMemo(() => {
    const rawValue = sectionName || titleForSection;
    if (!rawValue) return null;

    // slugify the input
    const sectionSlugToCheck = rawValue.toLowerCase().replace(/\s+/g, '-');

    // 1) Try matching by slug of section_name
    const bySlug =
      sectionsData?.find(
        section => section?.section_name.toLowerCase().replace(/\s+/g, '-') === sectionSlugToCheck,
      ) || null;

    if (bySlug) return bySlug;

    // 2) fallback: try matching by id directly in case user passes section_id
    return sectionsData?.find(section => section.section_id === rawValue) || null;
  }, [sectionsData, titleForSection, sectionName]);

  const totalPages = useMemo(() => {
    return Math.ceil((sectionToDisplay?.faq_data.length || 0) / ITEMS_PER_PAGE);
  }, [sectionToDisplay]);

  const currentFaqs = useMemo(() => {
    return sectionToDisplay?.faq_data.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE,
    );
  }, [sectionToDisplay, currentPage]);

  useEffect(() => {
    if (sectionToDisplay) {
      window.scrollTo(0, 0);
    }
  }, [sectionToDisplay]);
  useEffect(() => {
    if (sectionToDisplay?.section_id) {
      const cdnurl = process.env.REACT_APP_CDN_URL;
      const imageUrl = `${cdnurl}/section-images/${sectionToDisplay.section_id}.png`;

      // Optionally check if image exists
      fetch(imageUrl, { method: 'HEAD' })
        .then(res => {
          if (res.ok) {
            setSectionBannerUrl(imageUrl);
          } else {
            setSectionBannerUrl(null); // fallback or don't set
          }
        })
        .catch(() => {
          setSectionBannerUrl(null);
        });
    }
  });

  if (!sectionToDisplay) {
    return (
      <Box
        px={{ base: '2vw', md: '1vw', lg: '15vw' }}
        backgroundColor={settings?.configs.primaryBgColor}
        textAlign="center"
        py={8}
      >
        <Text fontSize="2xl" color={settings?.configs.secondaryTextColor}>
          404 - Article Not Found
        </Text>
        <Text fontSize="lg" color={settings?.configs.secondaryTextColor}>
          The requested article could not be found. Please check the URL or try again later.
        </Text>
      </Box>
    );
  }

  return (
    <Layout
      onClickLandingPageButton={onClickLandingPageButton}
      onClickMyTickets={onClickMyTickets}
      onContactUsClick={onContactUsClick}
      pageBgColor={settings?.configs.primaryBgColor}
      settings={settings}
      isPreviewMode
    >
      <Box
        height={{ base: '100px', md: '200px', lg: '300px' }} // Adjust height as needed
        backgroundImage={`url(${sectionBannerUrl || settings?.configs.bannerImageUrl})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: '4vw', md: '10vw', lg: '20vw' }}
      >
        <Box width="100%" display="flex" justifyContent="center">
          <Box width="100%">
            <SearchBar
              searchBarTextColor={settings?.configs.secondaryTextColor}
              placeholderText="Search for articles"
              searchBarPx={{ base: '5vw', md: '3vw', lg: '1vw' }}
              isPreview
            />
          </Box>
        </Box>
      </Box>

      <Box
        px={{ base: '2vw', md: '3vw', lg: '6vw' }}
        backgroundColor={settings?.configs.primaryBgColor}
        py={{ base: '2vh', lg: '2vh' }}
        overflow="visible"
      >
        <Flex mt={8} gap={8} flexDirection={{ base: 'column', lg: 'row' }}>
          <Box flex="2" backgroundColor="#fff" p={4}>
            <Breadcrumb separator="/" fontSize="xs" color={secondaryAccentColor} p={4}>
              <BreadcrumbItem>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => onClickLandingPageButton?.()}
                    color={secondaryAccentColor}
                  >
                    Home
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink color={secondaryAccentColor}>
                  {sectionToDisplay.section_name}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            <Box my={6} p={4}>
              <CustomText variant="h1" fontSize="4xl" color={settings?.configs.primaryTextColor}>
                {sectionToDisplay.section_name}
              </CustomText>
            </Box>

            {currentFaqs?.map(faq => (
              <Box
                key={faq.publish_id}
                borderBottom={`1px solid ${lightenedBorderColor}`}
                p={4}
                _hover={{ boxShadow: 'md', transform: 'scale(1.01)' }}
                // onClick={() => handleArticleClick(faq.publish_id)}
              >
                <Text
                  fontSize="md"
                  fontWeight="bold"
                  color={settings?.configs.secondaryTextColor}
                  noOfLines={1}
                >
                  {faq.faq_title}
                </Text>
                <Box
                  dangerouslySetInnerHTML={{ __html: faq.faq_body }}
                  color={settings?.configs.secondaryTextColor}
                  fontSize="sm"
                  style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                  }}
                />
              </Box>
            ))}

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                nextText={helperTexts.find((item: Record<string, string>) => 'next' in item)?.next}
                previousText={
                  helperTexts.find((item: Record<string, string>) => 'previous' in item)?.previous
                }
                pageText={helperTexts.find((item: Record<string, string>) => 'page' in item)?.page}
                ofText={helperTexts.find((item: Record<string, string>) => 'of' in item)?.of}
              />
            )}
          </Box>

          <Box
            flex="1"
            backgroundColor={settings?.configs.primaryBgColor}
            onClick={() => setCurrentPage(1)}
          >
            <Box mb={2}>
              <CustomText fontWeight="bold" variant="h1">
                {
                  categoryHeadings.find((item: Record<string, string>) => 'sectionsHeading' in item)
                    ?.sectionsHeading
                }
              </CustomText>
            </Box>
            <RelatedSectionsPreview
              // sectionName={sectionName}
              setSectionName={setSectionName}
              sectionsData={sectionsData}
              bgColor={settings?.configs.secondaryBgColor}
              textColor={settings?.configs.secondaryTextColor}
            />
          </Box>
        </Flex>
      </Box>
    </Layout>
  );
};
